import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import PopupCard from "../../../../Auth/PopupCard";
import ImportUI from "../../Users/DailogBoxes/ImportUI";
import LocationMapping from "./LocationMapping";
import {
  importLocation,
  uploadCsv,
} from "../../../../../redux/OrganizationSetup/organizationSetupSlice";

const supportedTypes = [".csv"];

const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getRowValueByHeader = (row, header) => {
  if (!row || typeof row !== "object") {
    return "";
  }

  if (Object.prototype.hasOwnProperty.call(row, header)) {
    return row[header];
  }

  const normalizedHeader = normalizeLabel(header);
  const matchedKey = Object.keys(row).find(
    (key) => normalizeLabel(key) === normalizedHeader,
  );

  return matchedKey ? row[matchedKey] : "";
};

const buildMappingRows = (uploadResponse) => {
  const payload = uploadResponse?.data || {};
  const csvHeaders = Array.isArray(payload?.csvHeaders)
    ? payload.csvHeaders
    : [];
  const sampleData = Array.isArray(payload?.sampleData)
    ? payload.sampleData
    : [];
  const dbFields = Array.isArray(payload?.dbFields) ? payload.dbFields : [];
  const suggestedMappings =
    payload?.suggestedMappings && typeof payload.suggestedMappings === "object"
      ? payload.suggestedMappings
      : {};

  const rows = csvHeaders.map((header) => ({
    header,
    samples: sampleData
      .map((row) => getRowValueByHeader(row, header))
      .filter(
        (item) => item !== undefined && item !== null && String(item).trim(),
      )
      .slice(0, 3)
      .map((item) => String(item)),
    suggestedAttribute: suggestedMappings[header] || "",
  }));

  return {
    rows,
    attributeOptions: dbFields,
    selections: rows.reduce((acc, row) => {
      acc[row.header] =
        suggestedMappings[row.header] || row.suggestedAttribute || "";
      return acc;
    }, {}),
  };
};

const formatApiError = (error, fallbackMessage) => {
  if (typeof error === "string") {
    return error;
  }

  const errors = Array.isArray(error?.errors)
    ? error.errors.filter(Boolean)
    : [];

  if (errors.length > 0) {
    return errors.join(", ");
  }

  return error?.message || fallbackMessage;
};

const getApiErrors = (error, fallbackMessage) => {
  if (typeof error === "string") {
    return [error];
  }

  const errors = Array.isArray(error?.errors)
    ? error.errors.filter((item) => item && String(item).trim())
    : [];

  if (errors.length > 0) {
    return errors.map((item) => String(item));
  }

  if (error?.message) {
    return [String(error.message)];
  }

  return [fallbackMessage];
};

const getMappedSelections = (mappingSelections) =>
  Object.entries(mappingSelections).reduce((acc, [header, attribute]) => {
    if (typeof attribute === "string" && attribute.trim()) {
      acc[header] = attribute;
    }

    return acc;
  }, {});

const ImportLocation = ({
  onClose,
  isMapping = false,
  heading = "Import Location",
  description = "Upload your CSV, validate the data, and map the columns before import.",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isMappingStep =
    isMapping || routerLocation.pathname.endsWith("/mapping");
  const routeState = routerLocation.state || {};
  const {
    loadingParams = {},
    uploadCsvData,
    importLocationData,
  } = useSelector((state) => state.orgSetup || {});
  const inputRef = useRef(null);
  const [documentName, setDocumentName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isFileValid, setIsFileValid] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const [currentStep, setCurrentStep] = useState("upload");
  const [mappingRows, setMappingRows] = useState([]);
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [mappingSelections, setMappingSelections] = useState({});
  const [popupConfig, setPopupConfig] = useState(null);
  const [hasSubmittedImport, setHasSubmittedImport] = useState(false);
  const [isAwaitingUploadResult, setIsAwaitingUploadResult] = useState(false);
  const [importResultData, setImportResultData] = useState(null);
  const isUploadingCsv = Boolean(loadingParams.csvUpload);
  const isImporting = Boolean(loadingParams.loadingLocation);

  // Reuse the hidden file input so both click and drag/drop can feed the same flow.
  const openFileDialog = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  // Backend can return multiple validation/import errors, so collect and show all of them.
  const showErrorPopup = (title, error, fallbackMessage) => {
    const errors = getApiErrors(error, fallbackMessage);

    setPopupConfig({
      type: "error",
      title,
      message: (
        <div className="space-y-2 text-left">
          {errors.map((item, index) => (
            <p key={`${title}_error_${index}`} className="text-sm leading-6">
              {item}
            </p>
          ))}
        </div>
      ),
    });
  };

  const buildFileFormData = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return formData;
  };

  // Selecting a new file resets the entire mapping/import flow and starts again from upload.
  const handleFileSelect = async (file) => {
    if (!file) return;

    setSelectedFile(file);
    setIsFileValid(true);
    setCurrentStep("upload");
    setValidationMessage("");
    setValidationError("");
    setDocumentName(file.name.replace(/\.[^/.]+$/, ""));
    setMappingRows([]);
    setAttributeOptions([]);
    setMappingSelections({});
    setHasSubmittedImport(false);
    setIsAwaitingUploadResult(false);
    setImportResultData(null);
  };

  const handleInputChange = (event) => {
    handleFileSelect(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelect(event.dataTransfer.files?.[0]);
  };

  // Step 1: upload the chosen CSV only when the user explicitly clicks Next.
  const handleNext = async () => {
    if (!selectedFile) return;

    setValidationMessage("");
    setValidationError("");
    setIsAwaitingUploadResult(true);

    try {
      const res = await dispatch(
        uploadCsv(buildFileFormData(selectedFile)),
      ).unwrap();
      setIsAwaitingUploadResult(false);
      const mappingData = buildMappingRows(res);
      navigate("/organization/organization-setup/import-location/mapping", {
        state: {
          selectedFile,
          documentName,
          uploadCsvData: res,
          mappingData,
        },
      });
    } catch (error) {
      setIsAwaitingUploadResult(false);
      setIsFileValid(false);
      setMappingRows([]);
      setAttributeOptions([]);
      setMappingSelections({});
      const errorMessage = formatApiError(error, "File upload failed.");
      setValidationError(errorMessage);
      showErrorPopup("Upload Failed", error, "File upload failed.");
    }
  };

  // If on mapping route, populate from routeState; if refreshed/no state, redirect to upload route
  useEffect(() => {
    if (isMappingStep) {
      if (routeState?.uploadCsvData && routeState?.selectedFile) {
        setSelectedFile(routeState.selectedFile);
        setDocumentName(
          routeState.documentName ||
            routeState.selectedFile.name.replace(/\.[^/.]+$/, ""),
        );
        const mappingData =
          routeState.mappingData || buildMappingRows(routeState.uploadCsvData);
        setMappingRows(mappingData.rows);
        setAttributeOptions(mappingData.attributeOptions);
        setMappingSelections(mappingData.selections);
        setIsFileValid(mappingData.rows.length > 0);
      } else {
        navigate("/organization/organization-setup/import-location", {
          replace: true,
        });
      }
    }
  }, [isMappingStep, routeState]);

  // If user changes a mapped attribute after validation, we force re-validation.
  const handleMappingSelectionChange = (header, value) => {
    setMappingSelections((prev) => ({
      ...prev,
      [header]: value,
    }));
    setImportResultData(null);
  };

  // Step 3: confirm and submit the final mapped file for import.
  const handleImport = async () => {
    setPopupConfig({
      type: "warning",
      title: "Confirm Import",
      message: "Do you want to import these mapped locations?",
      onConfirm: async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append(
          "columnMapping",
          JSON.stringify(getMappedSelections(mappingSelections)),
        );

        setValidationError("");
        setHasSubmittedImport(true);
        setPopupConfig(null);

        try {
          await dispatch(importLocation(formData)).unwrap();
        } catch (error) {
          setHasSubmittedImport(false);
          const errorMessage = formatApiError(error, "Import failed.");
          setValidationError(errorMessage);
          showErrorPopup("Import Failed", error, "Import failed.");
        }
      },
      onCancel: () => setPopupConfig(null),
    });
  };

  // Successful import should only react to the import triggered from this screen.
  useEffect(() => {
    if (!hasSubmittedImport || !importLocationData?.success) {
      return;
    }

    setImportResultData(importLocationData);

    const importPayload = importLocationData?.data || {};
    const importErrors = Array.isArray(importPayload?.errors)
      ? importPayload.errors
      : [];
    const validationErrors = Array.isArray(importPayload?.validationErrors)
      ? importPayload.validationErrors
      : [];
    const hasImportErrors =
      importErrors.length > 0 ||
      validationErrors.length > 0 ||
      Number(importPayload?.failedRows || 0) > 0 ||
      Number(importPayload?.failedCount || 0) > 0;

    setPopupConfig({
      type: "success",
      title: "Success",
      message:
        importLocationData?.message || "Locations imported successfully.",
    });

    const timeoutId = window.setTimeout(() => {
      setPopupConfig(null);
      setHasSubmittedImport(false);
      if (!hasImportErrors) {
        onClose();
      }
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hasSubmittedImport, importLocationData, onClose]);

  // Auto-close non-confirmation popups so users do not have to manually dismiss
  // error/info messages. Warning popup stays open because it needs a Yes/No action.
  useEffect(() => {
    if (!popupConfig || popupConfig.type === "warning") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setPopupConfig(null);
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [popupConfig]);

  const handleBack = () => {
    if (isMappingStep) {
      navigate("/organization/organization-setup/import-location");
      return;
    }

    onClose();
  };

  const showUploaderStep = !isMappingStep;

  return (
    <>
      <ImportUI
        heading={heading}
        description={description}
        downloadHref="/samples/location-import-sample.csv"
        downloadName="location-import-sample.csv"
        onBack={handleBack}
        inputRef={inputRef}
        accept={supportedTypes.join(",")}
        onInputChange={handleInputChange}
        showUploader={showUploaderStep}
        openFileDialog={openFileDialog}
        isDragging={isDragging}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        selectedFile={selectedFile}
        previewUrl={previewUrl}
        documentName={documentName}
        supportedTypes={supportedTypes}
        onCancel={onClose}
        onNext={handleNext}
        nextDisabled={!selectedFile || isUploadingCsv}
        nextLabel={isUploadingCsv ? "Uploading..." : "Next"}
      >
        <LocationMapping
          mappingRows={mappingRows}
          mappingSelections={mappingSelections}
          attributeOptions={attributeOptions}
          onSelectionChange={handleMappingSelectionChange}
          onCancel={() =>
            navigate("/organization/organization-setup/import-location")
          }
          onImport={handleImport}
          importResult={importResultData}
          isImporting={isImporting}
          validationError={validationError}
        />
      </ImportUI>
      {popupConfig && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 px-4">
          <PopupCard
            type={popupConfig.type}
            title={popupConfig.title}
            message={popupConfig.message}
            onConfirm={popupConfig.onConfirm}
            onCancel={popupConfig.onCancel}
            confirmText={popupConfig.confirmText}
            cancelText={popupConfig.cancelText}
          />
        </div>
      )}
    </>
  );
};

export default ImportLocation;
